import { ReactNode, useEffect, useState } from "react";
import useLocalStorage from "../../hooks/useLocalStorage";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DarkModeContext } from "../../contexts/DarkModeContext";
import { useGlobalOptions } from "../../hooks/useGlobalOptions";

/**
 * Perform initialization of things related to Themes and dark mode. Wrap children in a context provider that allows
 * them to consume and update the settings related to dark mode
 */
export const DarkModeEnabledProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { globalOptions } = useGlobalOptions();
  const [darkModeEnabledPreference, setDarkModeEnabledPreference] =
    useLocalStorage<boolean | null>("darkModeEnabledPreference", null);

  const [darkModeEnabled, setDarkModeEnabled] = useState<boolean>(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  useEffect(() => {
    const r: HTMLElement | null = document.querySelector(":root");
    r?.style.setProperty(
      "--primary-color",
      globalOptions.whitelabel_color ?? "#F20525",
    );
    document.title =
      (globalOptions.whitelabel_name ?? "Expedient") + " Chat";
  }, [globalOptions.whitelabel_color, globalOptions.whitelabel_name]);

  const darkTheme = createTheme({
    palette: {
      mode: darkModeEnabledPreference ?? darkModeEnabled ? "dark" : "light",
      primary: {
        main: globalOptions.whitelabel_color ?? "#F20525",
      },
      secondary: {
        main: "#E0C2FF",
        light: "#F5EBFF",
        contrastText: "#47008F",
      },
    },
  });

  const toggleDarkModeEnabled = () => {
    setDarkModeEnabledPreference((prev) => !(prev ?? darkModeEnabled));
  };
  return (
    <ThemeProvider theme={darkTheme}>
      <DarkModeContext.Provider
        value={{
          darkModeEnabled: darkModeEnabledPreference ?? darkModeEnabled,
          toggleDarkModeEnabled: toggleDarkModeEnabled,
        }}
      >
        {children}
      </DarkModeContext.Provider>
    </ThemeProvider>
  );
};
